import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode
} from "react";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { Show } from "shared/components";
import { cx } from "shared/utils/classnames";
import type { ComposerTab } from "./constants";
import { ComposerNarrowProvider } from "../context/ComposerNarrowContext";

interface ComposerFooterRowProps {
  tabs: Tab<ComposerTab>[];
  active: ComposerTab;
  onTabChange: (tab: ComposerTab) => void;
  showTabs?: boolean;
  leftExtra?: ReactNode;
  children?: ReactNode;
}

const TAB_BASE_CLASS =
  "[&_button]:rounded-full [&_button]:h-10 [&_button]:px-4 [&_button]:py-0 [&_button[aria-selected=false]]:text-content-300";

const TAB_ICON_ONLY_CLASS = "[&_button>span]:hidden [&_button]:px-3";

const WIDE = 0;
const ROUTING_HIDDEN = 2;

const useFooterLevel = (contentKey: string) => {
  const ref = useRef<HTMLDivElement>(null);
  const [level, setLevel] = useState(WIDE);
  const levelRef = useRef(WIDE);
  const neededWidthsRef = useRef([0, 0]);

  useLayoutEffect(() => {
    levelRef.current = WIDE;
    setLevel(WIDE);
  }, [contentKey]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      const current = levelRef.current;
      let next = current;
      if (current < ROUTING_HIDDEN && el.scrollWidth > el.clientWidth) {
        neededWidthsRef.current[current] = el.scrollWidth;
        next = current + 1;
      } else if (
        current > WIDE &&
        el.clientWidth >= neededWidthsRef.current[current - 1]
      )
        next = current - 1;
      if (next === current) return;
      levelRef.current = next;
      setLevel(next);
    });
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [contentKey]);

  return { ref, level };
};

export const ComposerFooterRow = ({
  tabs,
  active,
  onTabChange,
  showTabs = true,
  leftExtra,
  children
}: ComposerFooterRowProps) => {
  const hasLeftGroup = showTabs || Boolean(leftExtra);
  const { ref, level } = useFooterLevel(`${active}:${hasLeftGroup}`);
  const isNarrow = level !== WIDE;

  return (
    <div
      ref={ref}
      className="flex min-h-[56px] flex-row flex-nowrap items-center gap-4 px-6 py-2"
    >
      <Show if={hasLeftGroup}>
        <div className="flex min-w-0 shrink-0 items-center gap-4">
          <Show if={showTabs}>
            <Tabs
              tabs={tabs}
              active={active}
              onChange={onTabChange}
              tabStyle="ghost-pink"
              size="sm"
              fullWidthTabs={false}
              className={cx(
                "flex-nowrap",
                TAB_BASE_CLASS,
                isNarrow && TAB_ICON_ONLY_CLASS
              )}
            />
          </Show>
          {leftExtra}
        </div>
      </Show>
      <div
        className={cx(
          "ml-auto shrink-0 items-center",
          level === ROUTING_HIDDEN ? "hidden" : "flex"
        )}
      >
        <ComposerNarrowProvider isNarrow={isNarrow}>
          {children}
        </ComposerNarrowProvider>
      </div>
    </div>
  );
};
